import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-49 — Admin settings for the customer-facing notification message
 * that fires whenever a deposit / withdraw entry is edited by admin.
 *
 * Placeholders supported: {request_no} {old_amount} {new_amount}
 */
const EditNotificationSettings = ({ adminId }) => {
  const [template, setTemplate] = useState('');
  const [initial, setInitial] = useState('');
  const [defaultTpl, setDefaultTpl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/edit-notification-template`);
      setTemplate(r.data?.template || '');
      setInitial(r.data?.template || '');
      setDefaultTpl(r.data?.default || '');
    } catch { toast.error('Failed to load template'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/edit-notification-template`, {
        admin_id: adminId,
        template,
      });
      toast.success('✅ Template saved — will be used for next edit');
      setInitial(template);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  const resetToDefault = () => {
    if (!defaultTpl) return;
    setTemplate(defaultTpl);
  };

  if (loading) return <div className="p-4 text-slate-500">Loading…</div>;

  const dirty = template !== initial;
  const preview = template
    .replaceAll('{request_no}', 'D001-25')
    .replaceAll('{old_amount}', '500')
    .replaceAll('{new_amount}', '400');

  return (
    <div className="p-4 max-w-3xl">
      <h2 className="font-bold text-lg mb-1">✏️ Edit Entry Notification</h2>
      <p className="text-xs text-slate-500 mb-3">
        Jab bhi admin kisi deposit / withdraw entry ki amount edit karega, customer ke
        chat mein automatic yeh message aayega. Neeche placeholders use kar sakte ho:
      </p>
      <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
        <code className="px-2 py-0.5 bg-slate-100 rounded">{'{request_no}'}</code>
        <code className="px-2 py-0.5 bg-slate-100 rounded">{'{old_amount}'}</code>
        <code className="px-2 py-0.5 bg-slate-100 rounded">{'{new_amount}'}</code>
      </div>

      <textarea
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        rows={5}
        className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
        placeholder={defaultTpl}
        data-testid="edit-notification-template-input"
      />

      <div className="mt-3 border border-slate-200 rounded p-3 bg-slate-50">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Preview (sample values)</div>
        <div className="text-sm text-slate-700 whitespace-pre-wrap">{preview || <span className="italic text-slate-400">Template empty — default will be used.</span>}</div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-4 py-1.5 text-sm rounded bg-emerald-600 text-white font-semibold disabled:opacity-40"
          data-testid="edit-notification-save"
        >
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        <button
          onClick={resetToDefault}
          className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-700"
          data-testid="edit-notification-reset"
        >
          Reset to default
        </button>
        {dirty && <span className="text-[11px] text-amber-700">unsaved</span>}
      </div>
    </div>
  );
};

export default EditNotificationSettings;
